import { fetchWithAuth } from "../../../../../../api-client/api_client";
import { APIEndpointMethod } from "../../../../../../models/Entities";
import { showErrorToast, showSuccessToast } from "../../../../../shared/Toasts";
import { CommonModelDiffRunExecution, DiffRunFormData } from "../entities";
import { Dispatch, SetStateAction } from "react";

interface CreateCommonModelDiffRunProps {
  blueprintId: string | null;
  candidateBlueprintVersionId?: string;
  onSuccess?: () => void;
  refreshDiffRuns?: () => void;
  setCommonModelDiffRunExecutions?: Dispatch<SetStateAction<CommonModelDiffRunExecution[] | null>>;
}

const useCreateCommonModelDiffRun = ({
  blueprintId,
  candidateBlueprintVersionId,
  onSuccess,
  refreshDiffRuns,
  setCommonModelDiffRunExecutions,
}: CreateCommonModelDiffRunProps) => {
  const basePath = `/blueprints/${blueprintId}`;
  const headers = {
    "Content-Type": "application/json",
  };

  const createDiffRun = (params: DiffRunFormData) => {
    if (!blueprintId) {
      return;
    }

    const payload: any = {
      linked_account_id: params.linkedAccountId,
      disable_filter_by_date: params.disableFilterByDate,
      respect_linked_account_scopes: params.respectLinkedAccountScopes,
    };

    if (candidateBlueprintVersionId) {
      payload.candidate_blueprint_version_id = candidateBlueprintVersionId;
    }

    // conditionally append max_page_iterations and override_last_run_at to payload
    if (params.maxPageIterations != null) {
      payload.max_page_iterations = params.maxPageIterations;
    }

    if (params.maxLoopIterations != null) {
      payload.max_loop_iterations = params.maxLoopIterations;
    }

    if (params.overrideLastRunAt != null && params.overrideLastRunAt !== "") {
      payload.override_last_run_at = params.overrideLastRunAt;
    }

    fetchWithAuth({
      path: `${basePath}/run-common-model-diff`,
      method: APIEndpointMethod.POST,
      headers,
      body: payload,
      onResponse: (diffRun: CommonModelDiffRunExecution) => {
        if (setCommonModelDiffRunExecutions) {
          setCommonModelDiffRunExecutions((prevRuns) =>
            prevRuns ? [diffRun, ...prevRuns] : [diffRun]
          );
        }

        if (refreshDiffRuns) {
          refreshDiffRuns();
        }

        if (onSuccess) {
          onSuccess();
        }
        showSuccessToast("Common Model diff run successfully created");
      },
      onError: (error) => {
        error
          ?.json()
          .then((value: string | Record<string, any>) => {
            let errorMessage = "";
            if (typeof value == "string") {
              errorMessage = value;
            } else {
              errorMessage = value?.non_field_errors ?? JSON.stringify(value);
            }
            showErrorToast(
              `Failed to create Common Model diff run. Error message: \n ${errorMessage}`
            );
          })
          .catch(() => {
            showErrorToast("Failed to create Common Model diff run");
          });
      },
    });
  };

  return { createDiffRun };
};

export default useCreateCommonModelDiffRun;
